/**
 * The shape of one lesson on the training course (ROUND7 §5). A step says what to do in the trainee's own key
 * names, when it is worth saying (`ready`), and when it has been done (`done`). The coach shows the first step
 * that is neither done nor out of place, so a step done early is simply ticked when its turn comes, and a
 * station he walks past stops being mentioned instead of holding him up.
 */

import type { World } from '../../sim/types';
import type { Facts } from './facts';
import type { KeyNames } from './keys';

export type StepId =
  | 'kick' | 'jump' | 'steer'
  | 'move' | 'aim' | 'run' | 'crawl' | 'pod' | 'sneak' | 'knife'
  | 'trench' | 'dog' | 'carbine' | 'grenade' | 'wall' | 'recall' | 'ladder';

/** What a step may look at: the world after this tick, what the coach has seen, and his keys. */
export interface StepView {
  world: World;
  facts: Facts;
  keys: KeyNames;
}

export interface Step {
  id: StepId;
  /** The station, for the panel's heading: "THE DROP", "THE KNIFE". */
  station: string;
  /** A few words, for the tick when it is done: "KICK OUT A POD". */
  title: string;
  /** The instruction: one short sentence or two. */
  text(v: StepView): string;
  /** A second, dimmer line: why it matters, or the rule behind it. Empty for none. */
  note(v: StepView): string;
  /** Worth saying now: false while it cannot be done, or once its station is behind him. */
  ready(v: StepView): boolean;
  done(v: StepView): boolean;
}

/** Eight compass words for a ground offset (+x east, +z south). */
export function bearing(dx: number, dz: number): string {
  const words = ['NORTH', 'NORTH-EAST', 'EAST', 'SOUTH-EAST', 'SOUTH', 'SOUTH-WEST', 'WEST', 'NORTH-WEST'];
  // 0 = north, clockwise: north is -z, east is +x.
  const turn = Math.atan2(dx, -dz) / (Math.PI / 4);
  const index = ((Math.round(turn) % 8) + 8) % 8;
  return words[index] ?? 'NORTH';
}

/** On his feet on the ground and in the mission. */
export const onGround = (w: World): boolean => w.insertion.phase === 'ground' && w.result === null;
